import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

import { sentryLogger } from '@/adapters/logger/sentryLogger';
import { MIGRATION_FLAG_KEY } from '@/adapters/storage/migrateKeychainAccessibility';

const localStorageUnavailableMessage = 'Local storage is unavailable';

// Background recording can finish while the device is locked. The iOS default,
// WHEN_UNLOCKED, makes the keychain entry unreadable then, which fails the upload.
const secureStoreOptions: SecureStore.SecureStoreOptions = {
  keychainAccessible: SecureStore.AFTER_FIRST_UNLOCK,
};

const withWebStorage = <T>(action: (storage: Storage) => T, fallback: T): T => {
  try {
    if (typeof localStorage === 'undefined') {
      return fallback;
    }

    return action(localStorage);
  } catch (error) {
    sentryLogger.captureMessage(localStorageUnavailableMessage, 'warning');
    sentryLogger.captureException(error);
    return fallback;
  }
};

export type StorageAdapter = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  deleteItem: (key: string) => Promise<void>;
};

const createStorage = (): StorageAdapter => ({
  getItem: async (key: string): Promise<string | null> => {
    if (Platform.OS !== 'web') {
      return SecureStore.getItemAsync(key);
    }

    return withWebStorage((storageInstance) => storageInstance.getItem(key), null);
  },
  setItem: async (key: string, value: string): Promise<void> => {
    if (Platform.OS !== 'web') {
      try {
        return await SecureStore.setItemAsync(key, value, secureStoreOptions);
      } catch (error) {
        // Losing the write would clear the session, so degrade to the platform
        // default rather than let an unsupported accessibility level log the user out.
        sentryLogger.captureException(error, { context: 'secureStore.setItem.accessibility' });
        await SecureStore.setItemAsync(key, value);
        // This key is now back at the platform default and SecItemUpdate will never
        // lift it, so let the startup migration retry it on the next launch.
        await SecureStore.deleteItemAsync(MIGRATION_FLAG_KEY);

        return;
      }
    }

    return withWebStorage((storageInstance) => {
      storageInstance.setItem(key, value);
    }, undefined);
  },
  deleteItem: async (key: string): Promise<void> => {
    if (Platform.OS !== 'web') {
      return SecureStore.deleteItemAsync(key);
    }

    return withWebStorage((storageInstance) => {
      storageInstance.removeItem(key);
    }, undefined);
  },
});

export { createStorage };
